import React from 'react';
import { Shield, Sparkles, ArrowRight, Heart, Users, Calendar, BookOpen, Video } from 'lucide-react';

const ColorTestAmatista = () => {
  // Amatista variations for Facilitadoras
  const amatistaOptions = [
    {
      name: 'Amatista Profunda',
      primary: '#5a4a6a',
      primaryLight: '#6a5a7a',
      accent: '#c9a227',
      description: 'Púrpura amatista clásico, profundo y misterioso',
      vibe: 'Transformación, intuición, consciencia expandida'
    },
    {
      name: 'Amatista Cálida',
      primary: '#6a5a6a',
      primaryLight: '#7a6a7a',
      accent: '#c9a227',
      description: 'Amatista con toques tierra, más cálida',
      vibe: 'Espiritualidad encarnada, equilibrio'
    },
    {
      name: 'Amatista Rosada',
      primary: '#6a5068',
      primaryLight: '#7a6078',
      accent: '#c9a227',
      description: 'Amatista con matiz rosado, amorosa',
      vibe: 'Corazón, amor incondicional, sostén'
    },
    {
      name: 'Amatista Grisácea',
      primary: '#5f5565',
      primaryLight: '#6f6575',
      accent: '#c9a227',
      description: 'Amatista sofisticada, más neutra',
      vibe: 'Elegancia, sabiduría, neutralidad'
    },
    {
      name: 'Lavanda Oscura',
      primary: '#5d5474',
      primaryLight: '#6d6484',
      accent: '#c9a227',
      description: 'Lavanda profunda, serena',
      vibe: 'Calma profunda, meditación, paz'
    },
    {
      name: 'Ciruela Suave',
      primary: '#614051',
      primaryLight: '#715061',
      accent: '#c9a227',
      description: 'Ciruela madura, terrosa',
      vibe: 'Madurez, profundidad, intuición'
    }
  ];

  // Silver/Plata muy claro y difuminado for Límites
  const silverOptions = [
    {
      name: 'Plata Niebla',
      primary: '#a8aeb5',
      primaryLight: '#b8bec5',
      gradient: 'linear-gradient(135deg, #9aa0a8 0%, #b5bcc4 50%, #c8cfd7 100%)',
      accent: '#c9a227',
      textDark: '#4a5058',
      description: 'Plata muy clara como niebla matinal',
      vibe: 'Suavidad, protección sutil, claridad'
    },
    {
      name: 'Plata Perla',
      primary: '#b5b8c0',
      primaryLight: '#c5c8d0',
      gradient: 'linear-gradient(135deg, #a5a8b0 0%, #c0c4cc 50%, #d5d8e0 100%)',
      accent: '#c9a227',
      textDark: '#505560',
      description: 'Plata nacarada, luminosa',
      vibe: 'Pureza, luz interior, refinamiento'
    },
    {
      name: 'Plata Luna',
      primary: '#b0b5c0',
      primaryLight: '#c0c5d0',
      gradient: 'linear-gradient(135deg, #a0a5b5 0%, #bcc2d0 50%, #d0d5e0 100%)',
      accent: '#c9a227',
      textDark: '#4a4f5a',
      description: 'Plata azulada como luz de luna',
      vibe: 'Intuición, ciclos, feminino sagrado'
    },
    {
      name: 'Plata Arena',
      primary: '#b5b2aa',
      primaryLight: '#c5c2ba',
      gradient: 'linear-gradient(135deg, #a8a5a0 0%, #c0bcb5 50%, #d5d0c8 100%)',
      accent: '#c9a227',
      textDark: '#55524a',
      description: 'Plata cálida con matiz arena',
      vibe: 'Calidez, grounding, serenidad'
    },
    {
      name: 'Plata Cristal',
      primary: '#adb5ba',
      primaryLight: '#bdc5ca',
      gradient: 'linear-gradient(135deg, #9da5ac 0%, #b8c0c8 50%, #d0d8e0 100%)',
      accent: '#c9a227',
      textDark: '#4a5258',
      description: 'Plata cristalina, muy pura',
      vibe: 'Claridad mental, transparencia, verdad'
    },
    {
      name: 'Plata Humo',
      primary: '#a0a5a8',
      primaryLight: '#b0b5b8',
      gradient: 'linear-gradient(135deg, #909598 0%, #a8adb0 50%, #c0c5c8 100%)',
      accent: '#c9a227',
      textDark: '#484a4c',
      description: 'Plata ahumada, sutil',
      vibe: 'Misterio suave, protección, límites etéreos'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="text-4xl font-heading text-center mb-4">
          Pruebas: Amatista y Plata
        </h1>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          <strong>Facilitadoras:</strong> Variantes de amatista<br/>
          <strong>Límites:</strong> Plata muy claro y difuminado
        </p>

        {/* FACILITADORAS - AMATISTA */}
        <h2 className="text-2xl font-heading mb-8 pb-4 border-b-2 border-purple-200">
          💜 FACILITADORAS — Variantes de Amatista
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {amatistaOptions.map((palette, idx) => (
            <div key={idx} className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg">{palette.name}</h3>
                  <p className="text-sm text-gray-500">{palette.description}</p>
                  <p className="text-xs mt-1" style={{ color: palette.primary }}>✦ {palette.vibe}</p>
                </div>
                <div className="flex gap-2">
                  <div className="w-12 h-12 rounded-xl shadow" style={{ backgroundColor: palette.primary }} />
                  <div className="w-12 h-12 rounded-xl shadow" style={{ backgroundColor: palette.accent }} />
                </div>
              </div>

              <div 
                className="rounded-2xl overflow-hidden shadow-xl"
                style={{ background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryLight} 100%)` }}
              >
                <div className="p-6">
                  <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 mb-4">
                    <Sparkles className="w-3 h-3" style={{ color: palette.accent }} />
                    <span className="text-xs text-white/90">Formación Especial · 3ª Edición</span>
                  </div>
                  <h4 className="text-2xl font-heading text-white mb-2">Facilitadoras en Sostén Energético</h4>
                  <p className="text-white/70 text-sm mb-4">Sostener es un arte vibracional...</p>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 rounded-full text-xs font-medium text-white" style={{ backgroundColor: palette.accent }}>
                      Ver formación
                    </button>
                    <span className="text-xl font-heading" style={{ color: palette.accent }}>1.020€</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* LÍMITES - PLATA */}
        <h2 className="text-2xl font-heading mb-8 pb-4 border-b-2 border-gray-200">
          🪞 LÍMITES — Plata Claro y Difuminado
        </h2>
        
        <div className="space-y-10">
          {silverOptions.map((palette, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg">{palette.name}</h3>
                  <p className="text-sm text-gray-500">{palette.description}</p>
                  <p className="text-xs mt-1" style={{ color: palette.textDark }}>✦ {palette.vibe}</p>
                </div>
                <div className="flex gap-2">
                  <div className="w-12 h-12 rounded-xl shadow" style={{ background: palette.gradient }} />
                  <div className="w-12 h-12 rounded-xl shadow" style={{ backgroundColor: palette.accent }} />
                </div>
              </div>

              <div 
                className="rounded-2xl overflow-hidden shadow-lg"
                style={{ background: palette.gradient }}
              >
                <div className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <div 
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4"
                        style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                      >
                        <Shield className="w-4 h-4" style={{ color: palette.textDark }} />
                        <span className="text-sm" style={{ color: palette.textDark }}>Formación Especial · En vivo</span>
                      </div>
                      <h4 className="text-3xl font-heading mb-2" style={{ color: palette.textDark }}>LÍMITES</h4>
                      <p className="text-lg font-heading mb-4" style={{ color: palette.textDark, opacity: 0.7 }}>
                        La protección original de la consciencia
                      </p>
                      <p className="mb-6" style={{ color: palette.textDark, opacity: 0.8 }}>
                        Un master-curso encarnado para recuperar tu eje sin culpa, sin dureza y sin perder el corazón.
                      </p>
                      <div className="flex flex-wrap gap-4 mb-6">
                        <div className="flex items-center gap-2" style={{ color: palette.textDark, opacity: 0.7 }}>
                          <Video className="w-4 h-4" />
                          <span className="text-sm">8 sesiones en directo</span>
                        </div>
                        <div className="flex items-center gap-2" style={{ color: palette.textDark, opacity: 0.7 }}>
                          <Users className="w-4 h-4" />
                          <span className="text-sm">14 plazas</span>
                        </div>
                      </div>
                      <div className="flex gap-3 items-center">
                        <button 
                          className="px-6 py-3 rounded-full text-sm font-medium text-white shadow-lg"
                          style={{ backgroundColor: palette.accent }}
                        >
                          Solicitar acceso
                        </button>
                        <button 
                          className="px-6 py-3 rounded-full text-sm font-medium border-2"
                          style={{ borderColor: palette.textDark, color: palette.textDark }}
                        >
                          Ver detalles
                        </button>
                        <span className="text-2xl font-heading ml-2" style={{ color: palette.accent }}>420€</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center">
                      <div 
                        className="text-center p-8 rounded-2xl"
                        style={{ backgroundColor: 'rgba(255,255,255,0.4)' }}
                      >
                        <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: palette.textDark, opacity: 0.3 }} strokeWidth={1} />
                        <p className="font-heading italic" style={{ color: palette.textDark, opacity: 0.7 }}>
                          "El límite no separa.<br/>Protege la vida."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Light section preview */}
              <div className="mt-4 p-4 bg-gray-50 rounded-xl flex gap-3 items-center">
                <button 
                  className="px-4 py-2 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: palette.accent }}
                >
                  Botón dorado
                </button>
                <button 
                  className="px-4 py-2 rounded-full text-xs font-medium border"
                  style={{ borderColor: palette.textDark, color: palette.textDark }}
                >
                  Botón outline
                </button>
                <span className="text-sm" style={{ color: palette.textDark }}>
                  Texto sobre fondo claro
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Recomendaciones */}
        <div className="mt-16 p-8 bg-gradient-to-br from-purple-50 to-gray-50 rounded-3xl">
          <h2 className="text-2xl font-heading mb-6 text-center">🏆 Mis Favoritas</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h3 className="font-heading text-lg mb-4 text-purple-800">Para FACILITADORAS</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow" style={{ backgroundColor: '#6a5a6a' }} />
                  <div>
                    <p className="font-medium">🥇 Amatista Cálida</p>
                    <p className="text-xs text-gray-500">Equilibrio entre espiritualidad y tierra</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow" style={{ backgroundColor: '#6a5068' }} />
                  <div>
                    <p className="font-medium">🥈 Amatista Rosada</p>
                    <p className="text-xs text-gray-500">Conecta con el corazón y el sostén</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h3 className="font-heading text-lg mb-4 text-gray-700">Para LÍMITES</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow" style={{ background: 'linear-gradient(135deg, #a0a5b5, #d0d5e0)' }} />
                  <div>
                    <p className="font-medium">🥇 Plata Luna</p>
                    <p className="text-xs text-gray-500">Etéreo, protector, muy luminoso</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow" style={{ background: 'linear-gradient(135deg, #9aa0a8, #c8cfd7)' }} />
                  <div>
                    <p className="font-medium">🥈 Plata Niebla</p>
                    <p className="text-xs text-gray-500">Suave como niebla, muy protector</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Códigos */}
        <div className="mt-8 p-6 bg-gray-900 rounded-2xl text-white">
          <h3 className="font-heading mb-4">📋 Códigos Rápidos</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs font-mono">
            <div><span className="text-purple-400">Amatista Profunda:</span> #5a4a6a</div>
            <div><span className="text-purple-400">Amatista Cálida:</span> #6a5a6a</div>
            <div><span className="text-purple-400">Amatista Rosada:</span> #6a5068</div>
            <div><span className="text-gray-400">Plata Niebla:</span> #a8aeb5</div>
            <div><span className="text-gray-400">Plata Luna:</span> #b0b5c0</div>
            <div><span className="text-gray-400">Plata Arena:</span> #b5b2aa</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorTestAmatista;
